<template>
    <BaseDialog
        v-model:open="open"
        :title="t('teamPage.addTeamMembersTo', { name: report.name })"
        width="default"
        :show-close="!busy"
        mobile-height="expanded"
        dialog-content-class="fs-mask"
        :disable-close-on-backdrop-click="busy"
        :reserve-mobile-nav-space="false">
        <template #trigger
            ><BaseButton theme="neutral" class="base-btn-with-icon base-btn-icon-start" :disabled="disabled"
                ><i class="bi bi-plus-lg icon-semibold" aria-hidden="true" />{{ t("teamPage.addTeamMembers") }}</BaseButton
            ></template
        >
        <template v-if="report.add_members_url && report.member_search_url">
            <div class="flex flex-col gap-6 break-words">
                <section class="flex shrink-0 flex-col gap-3">
                    <FormField id="add-team-member-search" :label="t('peopleDirectory.createTeam.searchLabel')">
                        <BaseCombobox
                            v-model="candidate"
                            v-model:input="query"
                            :clearable="false"
                            :ignore-filter="true"
                            :options="options"
                            :key-value="(person) => person.id"
                            :display-value="(person) => `${person.name} ${person.email}`"
                            :placeholder="t('peopleDirectory.createTeam.searchPlaceholder')"
                            :disabled="busy || selected.length >= 100"
                            :actions="invitationActions"
                            @action="stageInvitation">
                            <template #item="{ option }">
                                <div class="flex items-center gap-3 fs-mask">
                                    <PeopleAvatar :name="option.name" size="sm" />
                                    <div class="min-w-0">
                                        <p class="body-small-semibold text-ink truncate">{{ option.name }}</p>
                                        <p class="details text-ink-muted truncate">{{ option.email }}</p>
                                    </div>
                                </div>
                            </template>
                            <template #empty
                                ><p v-if="!options.length && !invitationActions.length && searchStatus" class="body-small p-3 text-ink-muted">{{ searchStatus }}</p></template
                            >
                        </BaseCombobox>
                    </FormField>
                    <p class="sr-only" role="status">
                        <template v-if="searchStatus">{{ searchStatus }}</template>
                    </p>
                    <div class="empty:sr-only" role="alert">
                        <template v-if="searchError">
                            <p class="body-small text-ink">{{ t("peopleDirectory.createTeam.searchError") }}</p>
                            <BaseButton theme="secondary" size="sm" :disabled="busy || searching" @click="searchRequest?.execute()">{{ t("peopleDirectory.retry") }}</BaseButton>
                        </template>
                    </div>
                    <p v-if="selected.length" class="body-small text-ink-muted">{{ t("teamInvitations.reviewList") }}</p>
                    <BaseCard v-if="selected.length" padding="none" radius="md">
                        <ul class="max-h-64 divide-y divide-border overflow-y-auto">
                            <li v-for="person in selected" :key="selectionKey(person)" class="flex items-center gap-3 p-4">
                                <PeopleAvatar :name="selectionName(person)" size="sm" />
                                <div class="min-w-0 flex-1">
                                    <p class="body-small-semibold text-ink truncate">{{ selectionName(person) }}</p>
                                    <p class="details text-ink-muted truncate">{{ person.kind === "member" ? person.person.email : t("teamInvitations.emailInvitation") }}</p>
                                </div>
                                <BaseButton
                                    theme="neutral"
                                    size="sm"
                                    icon-only
                                    :disabled="busy"
                                    :aria-label="t('peopleDirectory.createTeam.remove', { name: selectionName(person) })"
                                    @click="selected = selected.filter((member) => selectionKey(member) !== selectionKey(person))">
                                    <i class="bi bi-x-lg" aria-hidden="true" />
                                </BaseButton>
                            </li>
                        </ul>
                    </BaseCard>
                    <div role="alert" class="empty:sr-only">
                        <template v-if="managementFailed">
                            <p class="body-small text-ink">{{ t("teamInvitations.loadError") }}</p>
                            <BaseButton theme="secondary" size="sm" :disabled="busy || managementLoading" @click="loadManagement">{{ t("peopleDirectory.retry") }}</BaseButton>
                        </template>
                        <template v-else-if="joinLinkRequest?.error.value">
                            <p class="body-small text-ink">{{ t("teamInvitations.linkLoadError") }}</p>
                            <BaseButton theme="secondary" size="sm" :disabled="busy || joinLinkRequest.isFetching.value" @click="joinLinkRequest.execute()">{{
                                t("peopleDirectory.retry")
                            }}</BaseButton>
                        </template>
                        <p v-else-if="copyError" class="body-small text-ink">{{ copyError }}</p>
                    </div>
                    <p v-if="emailInvitationsUnavailable" class="body-small text-ink">{{ t("teamInvitations.emailDisabled") }}</p>
                    <p role="status" class="sr-only">{{ copied ? t("teamInvitations.copied") : "" }}</p>
                    <div ref="addFeedback">
                        <BaseNotification theme="error" hide-dismiss class="empty:sr-only">
                            <template #content>
                                <p v-if="addError" class="body-small text-ink">{{ addError }}</p>
                            </template>
                        </BaseNotification>
                    </div>
                </section>
            </div>
        </template>
        <template #footer>
            <footer class="flex w-full flex-wrap items-center gap-3">
                <BaseButton v-if="isLead" theme="neutral" class="mr-auto" :disabled="busy || copying || !signupLink" @click="copyLink">{{
                    copied ? t("teamInvitations.copied") : t("teamInvitations.copyLink")
                }}</BaseButton>
                <div class="ml-auto flex gap-3">
                    <BaseButton theme="neutral" :disabled="busy" @click="open = false">{{ t("teamPerspective.cancel") }}</BaseButton>
                    <BaseButton theme="primary" :disabled="busy || !canSubmit" @click="addMembers">{{ busy ? t("teamPage.saving") : submitLabel }}</BaseButton>
                </div>
            </footer>
        </template>
    </BaseDialog>
    <!-- Outside the dialog so the confirmation survives it closing. -->
    <BaseToast v-model:open="addedToastOpen" theme="success" :description="addedMessage" :truncate-length="200" />
</template>
<script setup lang="ts">
import { refDebounced, type UseFetchReturn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { TeamInvitationCreated, TeamInvitationCreateRequest, TeamJoinLinkCreated } from "~vue/api/generated/team-invitations";
import type { TeamAddMembersRequest, TeamCandidate, TeamCandidateSearch, TeamPageNavigation, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import FormField from "~vue/components/form/FormField.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import { withTeamPageLinks } from "~vue/Perspective/teamReportNavigation";
import type { TeamInvitationManagementState } from "~vue/Perspective/useTeamInvitationManagement";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { z } from "zod";

const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    report,
    navigation,
    invitationManagement,
    disabled = false,
} = defineProps<{
    source?: TeamInteractionSource;
    report: TeamPerspectiveReport;
    navigation: TeamPageNavigation;
    invitationManagement: TeamInvitationManagementState;
    disabled?: boolean;
}>();
const emit = defineEmits<{ updated: [report: TeamPerspectiveReport]; completed: [] }>();
const { t } = useI18n();
const open = ref(false);
const busy = ref(false);

const addError = ref("");
const addFeedback = useTemplateRef<HTMLDivElement>("addFeedback");

const addedToastOpen = ref(false);
const addedMessage = ref("");
type TeamMemberSelection = { kind: "member"; person: TeamCandidate } | { kind: "invitation"; email: string; requestId: string };
const selected = ref<TeamMemberSelection[]>([]);
const members = computed(() => selected.value.filter((selection) => selection.kind === "member"));
const invitations = computed(() => selected.value.filter((selection) => selection.kind === "invitation"));
const isLead = computed(() => !!report.add_members_url && !!report.member_search_url && report.members.some((member) => member.id === report.viewer_id && member.role === "lead"));
const { management, loading: managementLoading, failed: managementFailed, load: loadManagement } = invitationManagement;
const copied = ref(false);
const copying = ref(false);
const copyError = ref("");
const joinLinkRequest = shallowRef<UseFetchReturn<TeamJoinLinkCreated>>();
const joinLinkUrl = computed(() => (open.value && isLead.value && management.value?.mode === "email" ? `/v1/teams/${report.id}/join-links` : null));
watch(joinLinkUrl, (url, _old, onCleanup) => {
    joinLinkRequest.value = undefined;
    if (!url) {
        return;
    }
    const request = useFetch(url, teamInteractionRequest(source), { immediate: false }).post().json<TeamJoinLinkCreated>();
    joinLinkRequest.value = request;
    onCleanup(() => request.abort());
    void request.execute();
});
const signupLink = computed(() => (management.value?.mode === "manual_link" ? management.value.url : joinLinkRequest.value?.data.value?.url));
const emailInvitationsUnavailable = computed(() => invitations.value.length > 0 && management.value?.mode === "manual_link");
const canSubmit = computed(() => members.value.length > 0 || (invitations.value.length > 0 && management.value?.mode === "email"));
const submitLabel = computed(() => {
    if (invitations.value.length && management.value?.mode === "email") {
        return members.value.length ? t("teamInvitations.addAndSend") : t("teamInvitations.sendInvites");
    }
    return t("teamPage.add");
});
function selectionKey(selection: TeamMemberSelection): string {
    return selection.kind === "member" ? `member:${selection.person.id}` : `invitation:${selection.email}`;
}
function selectionName(selection: TeamMemberSelection): string {
    return selection.kind === "member" ? selection.person.name : selection.email;
}
const candidate = ref<TeamCandidate | null>(null);
const query = ref("");
const debouncedQuery = refDebounced(query, 250);
const searchRequest = shallowRef<UseFetchReturn<TeamCandidateSearch>>();
const searchUrl = computed(() =>
    open.value && report.member_search_url && query.value === debouncedQuery.value && debouncedQuery.value.trim().length >= 2
        ? `${report.member_search_url}?${new URLSearchParams({ q: debouncedQuery.value.trim() })}`
        : null,
);
watch(searchUrl, (url, _old, onCleanup) => {
    searchRequest.value = undefined;
    if (!url) {
        return;
    }
    const request = useFetch(url, { immediate: false }).get().json<TeamCandidateSearch>();
    searchRequest.value = request;
    onCleanup(() => request.abort());
    void request.execute();
});
const searchError = computed(() => searchRequest.value?.error.value);
const searching = computed(() => query.value !== debouncedQuery.value || (searchRequest.value?.isFetching.value ?? false));
const options = computed(() => {
    if (searching.value || searchError.value || query.value.trim().length < 2) {
        return [];
    }
    const excluded = new Set([...report.members.map((member) => member.id), ...members.value.map((member) => member.person.id)]);
    const selectedEmails = new Set(selected.value.map((selection) => (selection.kind === "member" ? selection.person.email : selection.email).trim().toLowerCase()));
    return (searchRequest.value?.data.value?.results ?? []).filter((person) => !excluded.has(person.id) && !selectedEmails.has(person.email.trim().toLowerCase()));
});
const unmatchedEmail = computed(() => {
    const email = query.value.trim().toLowerCase();
    return !searching.value &&
        !searchError.value &&
        searchRequest.value?.data.value &&
        !searchRequest.value.data.value.results.length &&
        !selected.value.some((selection) => (selection.kind === "member" ? selection.person.email : selection.email).toLowerCase() === email) &&
        z.string().email().safeParse(email).success
        ? email
        : "";
});
const invitationActions = computed<ComboboxAction[]>(() =>
    management.value?.mode === "email" && unmatchedEmail.value && selected.value.length < 100
        ? [{ id: "invite-email", label: t("teamInvitations.addToEmailList", { email: unmatchedEmail.value }) }]
        : [],
);
function stageInvitation() {
    if (busy.value || !invitationActions.value.length) {
        return;
    }
    selected.value.push({ kind: "invitation", email: unmatchedEmail.value, requestId: crypto.randomUUID() });
    query.value = "";
}
async function copyLink() {
    if (!signupLink.value || copying.value || busy.value) {
        return;
    }
    copying.value = true;
    copied.value = false;
    copyError.value = "";
    try {
        await navigator.clipboard.writeText(signupLink.value);
        copied.value = true;
        logUserInteraction(AmplitudeEventNames.TEAM_JOIN_LINK_CLICKED, { source, team_id: report.id, interaction: "copy" });
    } catch {
        copyError.value = t("teamInvitations.copyError");
    } finally {
        copying.value = false;
    }
}
const searchStatus = computed(() => {
    if (selected.value.length >= 100) {
        return t("peopleDirectory.createTeam.limit");
    }
    const queryLength = query.value.trim().length;
    if (queryLength === 0) {
        return "";
    }
    // Matches the create form: the hint appears once someone starts typing rather than sitting under the field.
    if (queryLength < 2) {
        return t("peopleDirectory.createTeam.searchHint");
    }
    if (searching.value) {
        return t("peopleDirectory.createTeam.searching");
    }
    if (searchError.value) {
        return "";
    }
    if (options.value.length) {
        return t("peopleDirectory.createTeam.results", { count: options.value.length });
    }
    if (invitationActions.value.length) {
        return invitationActions.value[0]?.label ?? "";
    }
    return management.value?.mode === "manual_link" && searchRequest.value?.data.value?.results.length === 0
        ? t("teamInvitations.noMatchesManual")
        : t("peopleDirectory.createTeam.noMatches");
});
watch(candidate, async (person) => {
    if (!person || busy.value || selected.value.length >= 100) {
        return;
    }
    if (
        !selected.value.some((selection) =>
            selection.kind === "member"
                ? selection.person.id === person.id || selection.person.email.trim().toLowerCase() === person.email.trim().toLowerCase()
                : selection.email === person.email.trim().toLowerCase(),
        )
    ) {
        selected.value.push({ kind: "member", person });
    }
    await nextTick();
    candidate.value = null;
    query.value = "";
});
async function addMembers() {
    if (!report.add_members_url || busy.value || !canSubmit.value) {
        return;
    }
    busy.value = true;
    addError.value = "";
    const pendingMembers = members.value;
    const hadInvitations = invitations.value.length > 0;
    try {
        if (pendingMembers.length) {
            const payload: TeamAddMembersRequest = { member_ids: pendingMembers.map((selection) => selection.person.id) };
            const { data, error } = await useFetch(report.add_members_url, teamInteractionRequest(source)).post(payload).json<TeamPerspectiveReport>();
            if (error.value || !data.value) {
                addError.value = t("teamPage.addError");
                return;
            }
            selected.value = selected.value.filter((selection) => selection.kind !== "member");
            emit("updated", withTeamPageLinks(data.value, navigation));
        }
        for (const invitation of invitations.value) {
            if (management.value?.mode !== "email") {
                return;
            }
            logUserInteraction(AmplitudeEventNames.TEAM_INVITATION_STARTED, { source, team_id: report.id, is_resend: false });
            const payload: TeamInvitationCreateRequest = { email: invitation.email, request_id: invitation.requestId };
            const { data, error } = await useFetch(`/v1/teams/${report.id}/invitations`, teamInteractionRequest(source))
                .post(payload)
                .json<TeamInvitationCreated | TeamJoinLinkCreated>();
            if (error.value || !data.value) {
                addError.value = t("teamInvitations.remainingError");
                await loadManagement();
                return;
            }
            if (data.value.status === "share_link") {
                management.value = { mode: "manual_link", url: data.value.url };
                return;
            }
            selected.value = selected.value.filter((selection) => selectionKey(selection) !== selectionKey(invitation));
        }
        if (selected.value.length) {
            return;
        }
        await loadManagement();
        addedMessage.value = hadInvitations
            ? pendingMembers.length
                ? t("teamInvitations.addedAndSent")
                : t("teamInvitations.invitationsSent")
            : t("teamPage.addedToast", { count: pendingMembers.length, name: pendingMembers[0]?.person.name ?? "" });
        addedToastOpen.value = true;
        open.value = false;
        emit("completed");
    } catch {
        addError.value = t("teamInvitations.remainingError");
    } finally {
        busy.value = false;
        await nextTick();
        addFeedback.value?.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
}
watch(open, (isOpen) => {
    if (isOpen) {
        logUserInteraction(AmplitudeEventNames.TEAM_MANAGEMENT_STARTED, { source, team_id: report.id });
    }
    copied.value = false;
    copyError.value = "";
    selected.value = [];
    candidate.value = null;
    query.value = "";
    addError.value = "";
});
</script>
